import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCallJson } from './Api';

// ── Formatting helpers (same as CDPAnalyticsTab) ──
const fmt = (v, fb = '0') =>
  v == null || v === '' || Number.isNaN(v) ? fb : typeof v === 'number' ? v.toLocaleString() : String(v);
const fmtDec = (v, d = 1, fb = '0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d);
const fmtPct = (v, d = 1, fb = '0%') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d) + '%';
const fmtSign = (v, d = null, fb = '0') => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return fb;
  const n = Number(v);
  return (n >= 0 ? '+' : '') + (d !== null ? n.toFixed(d) : n.toLocaleString());
};

// ── Severity / direction config ──
const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  warning:  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  opportunity: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  info: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
};

const DIRECTION_LABEL = {
  improving: { label: 'Improving', color: 'text-green-600' },
  declining: { label: 'Declining', color: 'text-red-600' },
  stable:    { label: 'Stable', color: 'text-gray-500' },
  volatile:  { label: 'Volatile', color: 'text-amber-600' },
  insufficient_data: { label: 'Insufficient data', color: 'text-gray-400' },
};

const COHORT_TYPE_STYLES = {
  lifecycle:    { bg: 'bg-indigo-50', border: 'border-indigo-200', accent: 'text-indigo-700' },
  engagement:   { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700' },
  recency:      { bg: 'bg-sky-50', border: 'border-sky-200', accent: 'text-sky-700' },
  value:        { bg: 'bg-violet-50', border: 'border-violet-200', accent: 'text-violet-700' },
  completeness: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-700' },
};

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════

const CDPInsightsTab = ({ sessionId, currentVersion, user }) => {
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeView, setActiveView] = useState('insights');
  const [metricField, setMetricField] = useState('');
  const [metricTrend, setMetricTrend] = useState(null);
  const [metricAgg, setMetricAgg] = useState('avg');
  const [availableFields, setAvailableFields] = useState([]);
  const [campaignImpact, setCampaignImpact] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadInsights();
    loadAvailableFields();
    return () => { mountedRef.current = false; };
  }, [sessionId]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/insights`);
      if (mountedRef.current) setData(result);
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const loadAvailableFields = async () => {
    try {
      const result = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/insights/available-fields`);
      if (mountedRef.current) setAvailableFields(result.fields || []);
    } catch {
      // Fallback to old endpoint (returns string array)
      try {
        const result = await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/analytics/available-fields`);
        if (mountedRef.current) setAvailableFields((result.fields || []).map(f => ({ name: f, type: 'text' })));
      } catch { /* silent */ }
    }
  };

  const triggerCompute = async () => {
    try {
      setComputing(true);
      await apiCallJson(`/api/cdp-analytics/sessions/${sessionId}/insights/compute`, { method: 'POST' });
      // Poll for completion
      setTimeout(() => loadInsights(), 3000);
      setTimeout(() => { if (mountedRef.current) setComputing(false); }, 5000);
    } catch (err) {
      setComputing(false);
      setError(err.message);
    }
  };

  const loadMetricTrend = async () => {
    if (!metricField) return;
    try {
      const result = await apiCallJson(
        `/api/cdp-analytics/sessions/${sessionId}/insights/metric-trend/${metricField}?aggregation=${metricAgg}`
      );
      if (mountedRef.current) setMetricTrend(result);
    } catch (err) {
      if (mountedRef.current) setMetricTrend({ error: err.message });
    }
  };

  const loadCampaignImpact = async (campaignId) => {
    try {
      const result = await apiCallJson(
        `/api/cdp-analytics/sessions/${sessionId}/insights/campaign-impact/${campaignId}`
      );
      if (mountedRef.current) setCampaignImpact(result);
    } catch { /* silent */ }
  };

  // ── Tabs ──
  const TABS = [
    { id: 'insights', label: 'Actionable Insights' },
    { id: 'health', label: 'Health Scores' },
    { id: 'cohorts', label: 'Auto-Cohorts' },
    { id: 'growth', label: 'Growth Trends' },
    { id: 'quality', label: 'Data Quality' },
    { id: 'campaigns', label: 'Campaign Impact' },
    { id: 'explore', label: 'Metric Explorer' },
  ];

  // ── Loading / Error states ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-blue-600 mx-auto mb-4" />
          <p className="text-sm text-gray-500">Computing insights…</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-medium mb-2">Failed to load insights</p>
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button onClick={loadInsights} className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">CDP Intelligence</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Auto-generated insights, cohort discovery, and health scoring
            {data?.computed_at && (
              <span className="ml-2 text-gray-400">
                — computed {new Date(data.computed_at).toLocaleString()}
              </span>
            )}
            {data?.cached && <span className="ml-1 text-gray-400">(cached)</span>}
          </p>
        </div>
        <button
          onClick={triggerCompute}
          disabled={computing}
          className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {computing ? 'Computing…' : 'Recompute'}
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap space-x-1 px-4" aria-label="Insights tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`py-3 px-3 border-b-2 text-sm font-medium transition-colors ${
                  activeView === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeView === 'insights' && <InsightsView insights={data?.insights} />}
          {activeView === 'health' && <HealthScoresView data={data?.health_scores} />}
          {activeView === 'cohorts' && <AutoCohortsView cohorts={data?.auto_cohorts} />}
          {activeView === 'growth' && <GrowthTrendsView data={data?.growth_trends} />}
          {activeView === 'quality' && <DataQualityView data={data?.data_quality} />}
          {activeView === 'campaigns' && (
            <CampaignImpactView
              data={data?.campaign_analysis}
              impact={campaignImpact}
              onSelectCampaign={loadCampaignImpact}
            />
          )}
          {activeView === 'explore' && (
            <MetricExplorerView
              fields={availableFields}
              metricField={metricField}
              setMetricField={setMetricField}
              metricAgg={metricAgg}
              setMetricAgg={setMetricAgg}
              metricTrend={metricTrend}
              onAnalyze={loadMetricTrend}
            />
          )}
        </div>
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 1. ACTIONABLE INSIGHTS
// ══════════════════════════════════════════════════════════════

const InsightsView = ({ insights }) => {
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  if (!insights || insights.length === 0) {
    return <EmptyState message="No insights generated yet. Upload more data versions to unlock trend analysis." />;
  }

  const filtered = insights.filter(i =>
    (filterSeverity === 'all' || i.severity === filterSeverity) &&
    (filterCategory === 'all' || i.category === filterCategory)
  );

  const categories = [...new Set(insights.map(i => i.category))];
  const severityCounts = {
    critical: insights.filter(i => i.severity === 'critical').length,
    warning: insights.filter(i => i.severity === 'warning').length,
    opportunity: insights.filter(i => i.severity === 'opportunity').length,
    info: insights.filter(i => i.severity === 'info').length,
  };

  return (
    <div className="space-y-5">
      {/* Summary strip */}
      <div className="flex items-center space-x-4 text-sm">
        <span className="text-gray-500">{insights.length} insights</span>
        <span className="text-gray-300">|</span>
        {severityCounts.critical > 0 && (
          <span className="text-red-600 font-medium">{severityCounts.critical} critical</span>
        )}
        {severityCounts.warning > 0 && (
          <span className="text-amber-600 font-medium">{severityCounts.warning} warning</span>
        )}
        {severityCounts.opportunity > 0 && (
          <span className="text-blue-600 font-medium">{severityCounts.opportunity} opportunity</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-3">
        <select
          value={filterSeverity}
          onChange={e => setFilterSeverity(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <option value="all">All severity</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="opportunity">Opportunity</option>
          <option value="info">Info</option>
        </select>
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          <option value="all">All categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {/* Insight cards */}
      <div className="space-y-3">
        {filtered.map((insight, idx) => (
          <InsightCard key={insight.insight_id || idx} insight={insight} />
        ))}
      </div>
    </div>
  );
};

const InsightCard = ({ insight }) => {
  const [expanded, setExpanded] = useState(false);
  const s = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info;
  const dir = DIRECTION_LABEL[insight.trend] || {};

  return (
    <div className={`rounded-lg border ${s.border} ${s.bg} transition-all`}>
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2 flex-wrap">
                <span className={`font-semibold ${s.text}`}>{insight.title}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.badge}`}>
                  {insight.severity}
                </span>
                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                  {insight.category?.replace('_', ' ')}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{insight.description}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
            {insight.metric_value != null && (
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">{fmt(insight.metric_value)}</div>
                {insight.metric_change != null && (
                  <div className={`text-xs font-medium ${dir.color || 'text-gray-500'}`}>
                    {fmtSign(insight.metric_change, 1)}% {dir.label || ''}
                  </div>
                )}
              </div>
            )}
            <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-200/60 pt-3 ml-5">
          {/* Recommendation */}
          <div className="mb-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recommendation</div>
            <p className="text-sm text-gray-700 leading-relaxed">{insight.recommendation}</p>
          </div>

          <div className="flex items-center space-x-6 text-xs text-gray-500">
            {insight.affected_customers > 0 && (
              <span>{fmt(insight.affected_customers)} customers affected</span>
            )}
            {insight.priority_score != null && (
              <span>Priority score: {fmtDec(insight.priority_score, 0)}/100</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 2. HEALTH SCORES
// ══════════════════════════════════════════════════════════════

const HealthScoresView = ({ data }) => {
  const [tab, setTab] = useState('summary');

  if (!data || !data.summary) {
    return <EmptyState message="Health scores not yet computed. Requires at least 2 data versions." />;
  }

  const { summary, at_risk_customers, top_customers, score_distribution } = data;
  const riskDist = summary.risk_distribution || {};

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI label="Avg Health Score" value={fmtDec(summary.average_health_score, 1)} />
        <KPI label="Customers Scored" value={fmt(summary.total_scored)} />
        <KPI label="Healthy (low risk)" value={fmt(riskDist.low)} accent="text-green-600" />
        <KPI label="At Risk (high + critical)" value={fmt(summary.at_risk_count)} accent="text-red-600" />
        <KPI label="Medium Risk" value={fmt(riskDist.medium)} accent="text-amber-600" />
      </div>

      {/* Histogram */}
      {score_distribution && score_distribution.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Score Distribution</h4>
          <ScoreDistributionBars buckets={score_distribution} />
        </div>
      )}

      {/* Toggle at-risk vs top */}
      <div className="flex items-center space-x-2">
        <TabPill active={tab === 'summary'} onClick={() => setTab('summary')}>At-Risk Customers</TabPill>
        <TabPill active={tab === 'top'} onClick={() => setTab('top')}>Top Customers</TabPill>
      </div>

      {/* Customer table */}
      <CustomerHealthTable
        customers={tab === 'top' ? top_customers : at_risk_customers}
        emptyLabel={tab === 'top' ? 'No top customers' : 'No at-risk customers found'}
      />
    </div>
  );
};

const CustomerHealthTable = ({ customers, emptyLabel }) => {
  if (!customers || customers.length === 0) {
    return <EmptyState message={emptyLabel} />;
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Overall</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Engagement</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recency</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Consistency</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Risk</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Factors</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {customers.slice(0, 30).map((c, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-sm font-medium text-gray-900 truncate max-w-[200px]">
                  {c.customer_key}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <ScoreBadge score={c.overall_score} />
                </td>
                <td className="px-4 py-2.5 text-center text-sm text-gray-600">{fmtDec(c.engagement_score, 0)}</td>
                <td className="px-4 py-2.5 text-center text-sm text-gray-600">{fmtDec(c.recency_score, 0)}</td>
                <td className="px-4 py-2.5 text-center text-sm text-gray-600">{fmtDec(c.consistency_score, 0)}</td>
                <td className="px-4 py-2.5 text-center">
                  <RiskBadge level={c.risk_level} />
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[300px]">
                  {(c.risk_factors || []).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 3. AUTO-COHORTS
// ══════════════════════════════════════════════════════════════

const AutoCohortsView = ({ cohorts }) => {
  const [typeFilter, setTypeFilter] = useState('all');

  if (!cohorts || cohorts.length === 0) {
    return <EmptyState message="No auto-cohorts discovered. Requires data in the unified table." />;
  }

  const types = [...new Set(cohorts.map(c => c.cohort_type))];
  const filtered = typeFilter === 'all' ? cohorts : cohorts.filter(c => c.cohort_type === typeFilter);

  // Group by type
  const grouped = {};
  filtered.forEach(c => {
    if (!grouped[c.cohort_type]) grouped[c.cohort_type] = [];
    grouped[c.cohort_type].push(c);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{cohorts.length} cohorts discovered across {types.length} dimensions</p>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
        >
          <option value="all">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {Object.entries(grouped).map(([type, items]) => {
        const style = COHORT_TYPE_STYLES[type] || COHORT_TYPE_STYLES.lifecycle;
        return (
          <div key={type}>
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              {type.replace('_', ' ')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((cohort, idx) => (
                <div key={idx} className={`rounded-lg border ${style.border} ${style.bg} p-4`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-sm font-semibold ${style.accent}`}>{cohort.cohort_name}</span>
                    <span className="text-lg font-bold text-gray-900">{fmt(cohort.size)}</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed">{cohort.description}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 4. GROWTH TRENDS
// ══════════════════════════════════════════════════════════════

const GrowthTrendsView = ({ data }) => {
  if (!data || !data.total_customers) {
    return <EmptyState message="Growth trends require at least 2 data versions." />;
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Latest Total" value={fmt(data.latest_total)} />
        <KPI label="Latest New" value={fmt(data.latest_new)} />
        <KPI label="Versions" value={fmt(data.version_count)} />
        <KPI
          label="Growth Direction"
          value={DIRECTION_LABEL[data.total_customers?.trend?.direction]?.label || '—'}
          accent={DIRECTION_LABEL[data.total_customers?.trend?.direction]?.color}
        />
      </div>

      {/* Trend panels */}
      {['total_customers', 'new_customers', 'updated_customers'].map(key => {
        const section = data[key];
        if (!section) return null;
        const trend = section.trend || {};
        const values = section.values || [];
        const dir = DIRECTION_LABEL[trend.direction] || {};
        const label = key.replace('_', ' ').replace(/^\w/, c => c.toUpperCase());

        return (
          <div key={key} className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
              <span className={`text-sm font-medium ${dir.color || 'text-gray-500'}`}>
                {dir.label || '—'}
                {trend.slope != null && ` (${fmtSign(trend.slope, 1)}% / version)`}
              </span>
            </div>

            {/* Spark bar chart */}
            <SparkBars values={values.map(v => v[1])} labels={values.map(v => `v${v[0]}`)} />

            {/* Stats row */}
            <div className="flex items-center space-x-6 mt-3 text-xs text-gray-500">
              {trend.series_stats && (
                <>
                  <span>Min: {fmt(trend.series_stats.min)}</span>
                  <span>Max: {fmt(trend.series_stats.max)}</span>
                  <span>Mean: {fmt(trend.series_stats.mean)}</span>
                </>
              )}
              {trend.momentum != null && <span>Momentum: {fmtSign(trend.momentum, 1)}%</span>}
              {trend.volatility != null && <span>Volatility: {fmtDec(trend.volatility, 1)}%</span>}
              {trend.forecast_next != null && <span>Forecast next: {fmt(trend.forecast_next)}</span>}
            </div>

            {/* Anomalies */}
            {trend.anomalies && trend.anomalies.length > 0 && (
              <div className="mt-3 text-xs text-amber-600">
                Anomalies detected: {trend.anomalies.map(a => `${a.label} (${a.type}, z=${a.z_score})`).join(', ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 5. DATA QUALITY
// ══════════════════════════════════════════════════════════════

const DataQualityView = ({ data }) => {
  if (!data || !data.version_quality) {
    return <EmptyState message="Data quality tracking requires unified data." />;
  }

  const overallTrend = data.overall_trend || {};
  const dir = DIRECTION_LABEL[overallTrend.direction] || {};

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Latest Completeness" value={fmtPct(data.latest_completeness)} />
        <KPI label="Quality Trend" value={dir.label || '—'} accent={dir.color} />
        <KPI label="Improving Fields" value={fmt((data.improving_fields || []).length)} accent="text-green-600" />
        <KPI label="Degrading Fields" value={fmt((data.degrading_fields || []).length)} accent="text-red-600" />
      </div>

      {/* Version quality sparkline */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Completeness by Version</h4>
        <SparkBars
          values={data.version_quality.map(v => v.completeness_score)}
          labels={data.version_quality.map(v => `v${v.version}`)}
          color="bg-emerald-400"
        />
      </div>

      {/* Worst fields per version */}
      {data.version_quality.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Worst Fields (Latest Version)
          </h4>
          <div className="space-y-2">
            {(data.version_quality[data.version_quality.length - 1].worst_fields || []).map(([field, nullRate], idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <span className="text-sm text-gray-700 w-48 truncate">{field}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-red-400 h-2 rounded-full"
                    style={{ width: `${Math.min(nullRate, 100)}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-14 text-right">{fmtDec(nullRate, 1)}% null</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Degrading fields */}
      {data.degrading_fields && data.degrading_fields.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-red-700 mb-3">Degrading Fields (quality declining)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.degrading_fields.slice(0, 10).map((f, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-red-800">{f.field}</span>
                <span className="text-red-600 font-medium">
                  {fmtDec(f.initial_null_rate, 1)}% → {fmtDec(f.latest_null_rate, 1)}% null
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improving fields */}
      {data.improving_fields && data.improving_fields.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-green-700 mb-3">Improving Fields (quality rising)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.improving_fields.slice(0, 10).map((f, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-green-800">{f.field}</span>
                <span className="text-green-600 font-medium">
                  {fmtDec(f.initial_null_rate, 1)}% → {fmtDec(f.latest_null_rate, 1)}% null
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 6. CAMPAIGN IMPACT
// ══════════════════════════════════════════════════════════════

const CampaignImpactView = ({ data, impact, onSelectCampaign }) => {
  if (!data || !data.campaigns || data.campaigns.length === 0) {
    return <EmptyState message="No campaigns found. Create campaigns from the Campaigns tab to see impact analysis." />;
  }

  const { campaigns, best_performer, worst_performer, recommendations } = data;

  return (
    <div className="space-y-6">
      {/* Best / Worst comparison */}
      {best_performer && worst_performer && best_performer.campaign_id !== worst_performer.campaign_id && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <div className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-2">Best Performer</div>
            <div className="text-lg font-bold text-gray-900">{best_performer.campaign_name}</div>
            <div className="text-sm text-green-700 mt-1">
              {fmtPct(best_performer.conversion_rate)} conversion · {fmtSign(best_performer.roi_percentage, 1)}% ROI
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-5">
            <div className="text-xs text-red-600 font-semibold uppercase tracking-wide mb-2">Needs Improvement</div>
            <div className="text-lg font-bold text-gray-900">{worst_performer.campaign_name}</div>
            <div className="text-sm text-red-700 mt-1">
              {fmtPct(worst_performer.conversion_rate)} conversion · {fmtSign(worst_performer.roi_percentage, 1)}% ROI
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          {recommendations.map((r, idx) => (
            <p key={idx} className="text-sm text-blue-800 leading-relaxed">{r}</p>
          ))}
        </div>
      )}

      {/* Campaign list */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Targeted</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conv Rate</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ROI %</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {campaigns.map((c, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.campaign_name}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">{fmt(c.target_count)}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-600">{fmt(c.conversions)}</td>
                <td className="px-4 py-3 text-sm text-right">{fmtPct(c.conversion_rate)}</td>
                <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">${fmt(c.revenue)}</td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={c.roi_percentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {fmtSign(c.roi_percentage, 1)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onSelectCampaign(c.campaign_id)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Analyze
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Campaign deep dive */}
      {impact && (
        <CampaignImpactDetail impact={impact} onClose={() => {}} />
      )}
    </div>
  );
};

const CampaignImpactDetail = ({ impact }) => {
  if (impact.error) return null;

  const ratingColors = {
    excellent: 'bg-green-100 text-green-800',
    good: 'bg-blue-100 text-blue-800',
    average: 'bg-amber-100 text-amber-800',
    needs_improvement: 'bg-red-100 text-red-800',
  };

  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900">{impact.campaign_name}</h4>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ratingColors[impact.effectiveness_rating] || 'bg-gray-100'}`}>
          {(impact.effectiveness_rating || '').replace('_', ' ')}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Targeted" value={fmt(impact.target_count)} />
        <KPI label="Converters" value={fmt(impact.converters)} />
        <KPI label="Conversion Rate" value={fmtPct(impact.conversion_rate)} />
        <KPI label="Avg Value" value={`$${fmtDec(impact.avg_conversion_value, 2)}`} />
      </div>

      {/* Pre/post lift */}
      {impact.pre_post_analysis && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="text-xs text-indigo-600 font-semibold uppercase tracking-wide mb-2">Pre/Post Campaign Lift</div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Before:</span>{' '}
              <span className="font-semibold">{fmt(impact.pre_post_analysis.pre_campaign_customers)}</span>
            </div>
            <div>
              <span className="text-gray-500">After:</span>{' '}
              <span className="font-semibold">{fmt(impact.pre_post_analysis.post_campaign_customers)}</span>
            </div>
            <div>
              <span className="text-gray-500">Lift:</span>{' '}
              <span className={`font-semibold ${impact.pre_post_analysis.growth_lift_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmtSign(impact.pre_post_analysis.growth_lift_pct, 1)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {impact.recommendations && impact.recommendations.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recommendations</div>
          <ul className="space-y-1">
            {impact.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-gray-700 leading-relaxed pl-3 border-l-2 border-gray-200">{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// 7. METRIC EXPLORER
// ══════════════════════════════════════════════════════════════

const MetricExplorerView = ({ fields, metricField, setMetricField, metricAgg, setMetricAgg, metricTrend, onAnalyze }) => {
  // fields can be [{name, type}] or plain strings (fallback)
  const typedFields = fields.map(f => typeof f === 'string' ? { name: f, type: 'text' } : f);
  const selectedField = typedFields.find(f => f.name === metricField);
  const isNumeric = selectedField?.type === 'number';

  const handleFieldChange = (fieldName) => {
    setMetricField(fieldName);
    const field = typedFields.find(f => f.name === fieldName);
    if (field && field.type !== 'number') {
      setMetricAgg('count_distinct');
    } else if (field && field.type === 'number' && metricAgg === 'count_distinct') {
      setMetricAgg('avg');
    }
  };

  const TYPE_BADGE = { number: 'bg-blue-100 text-blue-700', text: 'bg-gray-100 text-gray-600', datetime: 'bg-purple-100 text-purple-700', date: 'bg-purple-100 text-purple-700' };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Select a field to analyze its trend across versions. Numeric fields support all aggregations; text fields use distinct count.
      </p>

      <div className="flex items-end space-x-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Field</label>
          <select
            value={metricField}
            onChange={e => handleFieldChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Select a field…</option>
            {typedFields.map(f => (
              <option key={f.name} value={f.name}>
                {f.name} ({f.type === 'number' ? '#' : f.type === 'datetime' || f.type === 'date' ? '📅' : 'Aa'})
              </option>
            ))}
          </select>
        </div>
        <div className="w-40">
          <label className="block text-xs font-medium text-gray-700 mb-1">Aggregation</label>
          <select
            value={metricAgg}
            onChange={e => setMetricAgg(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="avg" disabled={!isNumeric && metricField}>Average {!isNumeric && metricField ? '(numeric only)' : ''}</option>
            <option value="sum" disabled={!isNumeric && metricField}>Sum {!isNumeric && metricField ? '(numeric only)' : ''}</option>
            <option value="min">Minimum</option>
            <option value="max">Maximum</option>
            <option value="count_distinct">Distinct Count</option>
          </select>
        </div>
        <button
          onClick={onAnalyze}
          disabled={!metricField}
          className="px-5 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 disabled:opacity-40 transition-colors"
        >
          Analyze
        </button>
      </div>

      {selectedField && (
        <div className="flex items-center space-x-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[selectedField.type] || TYPE_BADGE.text}`}>
            {selectedField.type}
          </span>
          {!isNumeric && metricField && (
            <span className="text-xs text-amber-600">Text field — only Distinct Count, Min, and Max are available</span>
          )}
        </div>
      )}

      {metricTrend && !metricTrend.error && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">
              {metricTrend.field} ({metricTrend.aggregation})
            </h4>
            {metricTrend.trend && (
              <span className={`text-sm font-medium ${DIRECTION_LABEL[metricTrend.trend.direction]?.color || 'text-gray-500'}`}>
                {DIRECTION_LABEL[metricTrend.trend.direction]?.label || '—'}
                {metricTrend.trend.slope != null && ` · ${fmtSign(metricTrend.trend.slope, 1)}%/ver`}
              </span>
            )}
          </div>

          <SparkBars
            values={metricTrend.data_points?.map(p => p[1]) || []}
            labels={metricTrend.data_points?.map(p => `v${p[0]}`) || []}
            color="bg-indigo-400"
          />

          {metricTrend.trend?.series_stats && (
            <div className="flex items-center space-x-6 text-xs text-gray-500">
              <span>Min: {fmtDec(metricTrend.trend.series_stats.min, 2)}</span>
              <span>Max: {fmtDec(metricTrend.trend.series_stats.max, 2)}</span>
              <span>Mean: {fmtDec(metricTrend.trend.series_stats.mean, 2)}</span>
              <span>Volatility: {fmtDec(metricTrend.trend.volatility, 1)}%</span>
            </div>
          )}
        </div>
      )}

      {metricTrend?.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {metricTrend.error}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ══════════════════════════════════════════════════════════════

const KPI = ({ label, value, accent }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-xl font-bold ${accent || 'text-gray-900'}`}>{value}</div>
  </div>
);

const TabPill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
      active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

const ScoreBadge = ({ score }) => {
  const num = Number(score);
  const color =
    num >= 70 ? 'bg-green-100 text-green-800' :
    num >= 50 ? 'bg-amber-100 text-amber-800' :
    num >= 30 ? 'bg-orange-100 text-orange-800' :
    'bg-red-100 text-red-800';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${color}`}>
      {fmtDec(score, 0)}
    </span>
  );
};

const RiskBadge = ({ level }) => {
  const colors = {
    low: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[level] || 'bg-gray-100 text-gray-600'}`}>
      {level}
    </span>
  );
};

const SparkBars = ({ values, labels, color = 'bg-blue-400' }) => {
  // Hooks must run on every render, before any early return.
  const gradId = React.useMemo(
    () => `spark-grad-${Math.random().toString(36).slice(2, 9)}`,
    []
  );
 
  if (!values || values.length === 0) return null;
 
  const numericValues = values.map(v => Number(v) || 0);
 
  if (numericValues.length === 1) {
    const v = numericValues[0];
    return (
      <div className="h-32 flex flex-col items-center justify-center">
        <span className="text-3xl font-semibold text-gray-700">
          {typeof v === 'number' ? v.toLocaleString() : v}
        </span>
        {labels?.[0] && (
          <span className="text-xs text-gray-400 mt-2">{labels[0]}</span>
        )}
        <span className="text-[10px] text-amber-600 mt-3">
          Only 1 data point — add more versions to see a trend
        </span>
      </div>
    );
  }
 
  // Tailwind bg-* -> hex for SVG strokes/fills
  const COLOR_MAP = {
    'bg-blue-400':    '#60a5fa',
    'bg-indigo-400':  '#818cf8',
    'bg-emerald-400': '#34d399',
    'bg-red-400':     '#f87171',
    'bg-amber-400':   '#fbbf24',
    'bg-purple-400':  '#c084fc',
  };
  const stroke = COLOR_MAP[color] || '#60a5fa';
 
  // SVG geometry (viewBox scales uniformly to parent width)
  const W = 400;
  const H = 140;
  const padL = 28;
  const padR = 28;
  const padT = 22;
  const padB = 22;
 
  const minV = Math.min(...numericValues, 0);
  const maxV = Math.max(...numericValues, 0);
  const range = (maxV - minV) || 1;
  const mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
 
  const xAt = (idx) =>
    numericValues.length === 1
      ? W / 2
      : padL + (idx / (numericValues.length - 1)) * (W - padL - padR);
 
  const yAt = (val) =>
    padT + (1 - (val - minV) / range) * (H - padT - padB);
 
  const points = numericValues.map((v, i) => ({ x: xAt(i), y: yAt(v), v, i }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath =
    `${linePath} L ${points[points.length - 1].x} ${H - padB} ` +
    `L ${points[0].x} ${H - padB} Z`;
 
  const meanY = yAt(mean);
  const zeroY = minV < 0 && maxV > 0 ? yAt(0) : null;
 
  return (
    <div className="w-full" style={{ aspectRatio: `${W} / ${H}`, maxHeight: '160px' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-full"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
          </linearGradient>
        </defs>
 
        {/* Zero reference (only when zero is in-range) */}
        {zeroY != null && (
          <line
            x1={padL} x2={W - padR} y1={zeroY} y2={zeroY}
            stroke="#d1d5db" strokeWidth="1" strokeDasharray="2 2"
          />
        )}
 
        {/* Mean reference line + label */}
        <line
          x1={padL} x2={W - padR} y1={meanY} y2={meanY}
          stroke="#9ca3af" strokeWidth="1" strokeDasharray="3 3"
          opacity="0.6"
        />
        <text
          x={W - padR} y={meanY - 3}
          textAnchor="end" fontSize="8" fill="#9ca3af"
        >
          mean {mean.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </text>
 
        {/* Area under the line */}
        <path d={areaPath} fill={`url(#${gradId})`} />
 
        {/* The line itself */}
        <path
          d={linePath}
          fill="none" stroke={stroke}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        />
 
        {/* Data points with labels */}
        {points.map((p) => (
          <g key={p.i}>
            <circle cx={p.x} cy={p.y} r="8" fill="transparent" />
            <circle
              cx={p.x} cy={p.y} r="3"
              fill="white" stroke={stroke} strokeWidth="1.5"
            />
            <text
              x={p.x} y={p.y - 7}
              textAnchor="middle" fontSize="9"
              fontWeight="500" fill="#374151"
            >
              {typeof p.v === 'number'
                ? p.v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : p.v}
            </text>
            {labels?.[p.i] && (
              <text
                x={p.x} y={H - 4}
                textAnchor="middle" fontSize="9" fill="#6b7280"
              >
                {labels[p.i]}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

const ScoreDistributionBars = ({ buckets }) => {
  if (!buckets || buckets.length === 0) return null;
 
  const total = buckets.reduce((sum, b) => sum + (b.count || 0), 0);
  const maxCount = Math.max(...buckets.map(b => b.count || 0), 1);
  const meanCount = total / buckets.length;
 
  // SVG geometry
  const W = 480;
  const H = 160;
  const padL = 12;
  const padR = 12;
  const padT = 28;     // room for "15 · 65%" label above bars
  const padB = 32;     // room for "0-20" label below bars
  const chartH = H - padT - padB;
 
  const barSlotW = (W - padL - padR) / buckets.length;
  const barW = barSlotW * 0.72;
  const barGap = barSlotW - barW;
 
  // Red → Green ramp matching the original palette intent
  const COLORS = ['#f87171', '#fb923c', '#fbbf24', '#60a5fa', '#34d399'];
  const ZERO_FILL = '#f9fafb';
  const ZERO_STROKE = '#e5e7eb';
 
  const yAt = (count) => padT + (1 - count / maxCount) * chartH;
  const meanY = yAt(meanCount);
 
  return (
    <div className="w-full" style={{ aspectRatio: `${W} / ${H}`, maxHeight: '200px' }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
        {/* Baseline */}
        <line
          x1={padL} x2={W - padR} y1={H - padB} y2={H - padB}
          stroke="#e5e7eb" strokeWidth="1"
        />
 
        {/* Mean reference line */}
        {total > 0 && meanCount > 0 && (
          <>
            <line
              x1={padL} x2={W - padR} y1={meanY} y2={meanY}
              stroke="#9ca3af" strokeWidth="1" strokeDasharray="3 3" opacity="0.6"
            />
            <text
              x={W - padR} y={meanY - 3}
              textAnchor="end" fontSize="8" fill="#9ca3af"
            >
              mean {meanCount.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </text>
          </>
        )}
 
        {/* Bars */}
        {buckets.map((bucket, idx) => {
          const count = bucket.count || 0;
          const isZero = count === 0;
          const barH = isZero ? 6 : Math.max((count / maxCount) * chartH, 4);
          const x = padL + idx * barSlotW + barGap / 2;
          const y = H - padB - barH;
          const pctOfTotal = total > 0 ? (count / total) * 100 : 0;
 
          return (
            <g key={idx}>
              {/* Value label */}
              <text
                x={x + barW / 2} y={y - 8}
                textAnchor="middle" fontSize="9"
                fontWeight="500" fill="#374151"
              >
                {count}
                {total > 0 && count > 0 && (
                  <tspan fill="#9ca3af" fontWeight="400">
                    {` · ${pctOfTotal.toFixed(0)}%`}
                  </tspan>
                )}
              </text>
 
              {/* Bar */}
              {isZero ? (
                <rect
                  x={x} y={y} width={barW} height={barH}
                  fill={ZERO_FILL}
                  stroke={ZERO_STROKE} strokeWidth="1"
                  strokeDasharray="2 2"
                  rx="2"
                />
              ) : (
                <rect
                  x={x} y={y} width={barW} height={barH}
                  fill={COLORS[idx] || '#9ca3af'}
                  rx="2"
                />
              )}
 
              {/* Range label */}
              <text
                x={x + barW / 2} y={H - padB + 14}
                textAnchor="middle" fontSize="9" fill="#6b7280"
              >
                {bucket.range}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-12 text-center">
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);


export default CDPInsightsTab;
